import { Crop169Rounded, CropDinRounded, RestartAltRounded } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Box, Divider, Grid, IconButton, Modal, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { LoadingComp } from "../loading/Loading";

interface ModalPreviewDocumentProps {
	open: boolean;
	onClose: () => void;
	previewUrl: string | null;
	previewDoc: any;
	isLoading: boolean;
}

export const ModalPreviewDocument = ({ open, onClose, previewUrl, previewDoc, isLoading }: ModalPreviewDocumentProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [zoom, setZoom] = useState(1);

	const toggleExpand = () => setIsExpanded((prev) => !prev);
	const handleZoomIn = () => setZoom((prev) => prev + 0.1);
	const handleZoomOut = () => setZoom((prev) => Math.max(0.1, prev - 0.1));
	const handleZoomReset = () => setZoom(1);

	// Resetar zoom e expandido ao abrir a modal
	useEffect(() => {
		if (open) {
			setZoom(1);
			setIsExpanded(false); // opcional: sempre começa compactada
		}
	}, [open]);

	return (
		<>
			<LoadingComp isopen={isLoading} />
			<Modal open={open} onClose={onClose}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 3,
						width: isExpanded ? "90%" : "85%",
						maxWidth: isExpanded ? "90%" : "80%",
						maxHeight: "90%",
						borderRadius: 3,
						transition: "all 0.3s ease",
						display: "flex",
						flexDirection: "column",
					}}
				>
					{/* Cabeçalho fixo */}
					<Grid container justifyContent="space-between" alignItems="center">
						<Typography variant="h6" fontWeight={500}>
							Visualizar documento
						</Typography>

						<Stack direction="row" spacing={1} alignItems="center">
							<Typography variant="body2" color="text.secondary">
								{Math.round(zoom * 100)}%
							</Typography>

							<Tooltip title="Reduzir zoom">
								<IconButton onClick={handleZoomOut} size="small" color="warning">
									<ZoomOutIcon fontSize="medium" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Aumentar zoom">
								<IconButton onClick={handleZoomIn} size="small" color="success">
									<ZoomInIcon fontSize="medium" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Resetar zoom">
								<IconButton onClick={handleZoomReset} size="small" color="primary">
									<RestartAltRounded fontSize="medium" />
								</IconButton>
							</Tooltip>
							<Tooltip title={isExpanded ? "Reduzir visualização" : "Expandir visualização"}>
								<IconButton onClick={toggleExpand} size="small">
									{isExpanded ? <Crop169Rounded fontSize="small" /> : <CropDinRounded fontSize="small" />}
								</IconButton>
							</Tooltip>
							<IconButton onClick={onClose} size="small">
								<CloseIcon />
							</IconButton>
						</Stack>
					</Grid>

					<Divider sx={{ my: 2 }} />

					{/* Área com scroll */}
					<Box
						sx={{
							overflowY: "auto",
							maxHeight: "calc(90vh - 100px)",
							pr: 1,
						}}
					>
						{isLoading && <Typography>Carregando documento...</Typography>}

						{!isLoading && previewUrl && (
							<>
								{previewDoc?.mimetype?.includes("image") ? (
									<Box sx={{ overflow: "auto" }}>
										<Box
											component="img"
											src={previewUrl}
											alt="preview"
											sx={{
												transform: `scale(${zoom})`,
												transformOrigin: "top left",
												transition: "transform 0.3s",
												display: "block",
											}}
										/>
									</Box>
								) : previewDoc?.mimetype?.includes("pdf") ? (
									<Box component="iframe" src={previewUrl} title="PDF Preview" width="100%" height="700px" style={{ border: "none" }} />
								) : (
									<Typography>Tipo de documento não suportado para visualização.</Typography>
								)}
							</>
						)}
					</Box>
				</Box>
			</Modal>
		</>
	);
};
